import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "serviceFilter",
    "linesFilter",
    "searchFilter",
    "autoRefresh",
    "refreshStatus",
    "refreshBtn",
    "logContainer",
    "logContent",
    "currentService",
    "initialData"
  ]

  static values = {
    instanceSlug: String
  }

  connect() {
    console.log("Container logs controller connected", this.instanceSlugValue)
    this.lastRefresh = new Date()
    this.logsData = {}

    // Load initial data
    if (this.hasInitialDataTarget) {
      try {
        this.logsData = JSON.parse(this.initialDataTarget.textContent)
        console.log("Loaded initial logs data for services:", Object.keys(this.logsData))
      } catch (e) {
        console.error("Failed to parse initial logs data:", e)
      }
    }

    // Start auto-refresh if enabled
    if (this.hasAutoRefreshTarget && this.autoRefreshTarget.checked) {
      console.log("Starting auto-refresh")
      this.startAutoRefresh()
    }

    // Update refresh status every second
    this.statusInterval = setInterval(() => this.updateRefreshStatus(), 1000)

    // Scroll to bottom initially
    this.scrollToBottom()
  }

  disconnect() {
    this.stopAutoRefresh()
    if (this.statusInterval) {
      clearInterval(this.statusInterval)
    }
  }

  changeService() {
    const service = this.serviceFilterTarget.value

    // Check if we have cached logs for this service
    if (service === 'all') {
      this.displayAllLogs()
      if (this.hasCurrentServiceTarget) {
        this.currentServiceTarget.textContent = 'all'
      }
    } else if (this.logsData[service]) {
      this.displayLogs(this.logsData[service])
      if (this.hasCurrentServiceTarget) {
        this.currentServiceTarget.textContent = service
      }
    }

    // Also fetch fresh logs
    this.refresh()
  }

  displayAllLogs() {
    // Combine logs from all services with service name prefix
    const allLogs = []
    const services = Object.keys(this.logsData).sort()

    services.forEach(service => {
      const logs = this.logsData[service]
      if (logs) {
        logs.split('\n').forEach(line => {
          if (line.trim()) {
            allLogs.push({ service, line })
          }
        })
      }
    })

    if (allLogs.length === 0) {
      this.logContentTarget.innerHTML = '<span class="opacity-50">No logs available</span>'
      return
    }

    const html = allLogs.map(({ service, line }) => {
      let className = ''
      if (/error|Error|ERROR|exception|Exception|EXCEPTION|fatal|Fatal|FATAL/i.test(line)) {
        className = 'text-error'
      } else if (/warn|Warn|WARN/i.test(line)) {
        className = 'text-warning'
      } else if (/info|Info|INFO/i.test(line)) {
        className = 'text-info'
      } else if (/debug|Debug|DEBUG/i.test(line)) {
        className = 'opacity-60'
      }
      return `<div class="${className} hover:bg-white/5">${this.escapeHtml(line)}</div>`
    }).join('')

    this.logContentTarget.innerHTML = html
  }

  async refresh() {
    if (!this.hasServiceFilterTarget || !this.hasLinesFilterTarget) {
      console.warn("Container logs: missing required targets")
      return
    }

    const service = this.serviceFilterTarget.value
    const lines = this.linesFilterTarget.value

    // Show loading state
    if (this.hasRefreshBtnTarget) {
      this.refreshBtnTarget.classList.add("loading")
    }

    try {
      const wasAtBottom = this.isScrolledToBottom()

      if (service === 'all') {
        // Fetch logs from all services
        await this.refreshAllServices(lines)
        this.displayAllLogs()
        if (this.hasCurrentServiceTarget) {
          this.currentServiceTarget.textContent = 'all'
        }
      } else {
        // Fetch logs from single service
        const url = `/instances/${this.instanceSlugValue}/logs?service=${service}&lines=${lines}`
        const response = await fetch(url, {
          headers: { 'Accept': 'text/plain' },
          credentials: 'same-origin'
        })

        if (response.ok) {
          const logs = await response.text()
          this.logsData[service] = logs
          this.displayLogs(logs)
          if (this.hasCurrentServiceTarget) {
            this.currentServiceTarget.textContent = service
          }
        } else {
          console.error("Failed to fetch logs:", response.status)
          if (this.hasRefreshStatusTarget) {
            this.refreshStatusTarget.textContent = `Error: ${response.status}`
          }
          return
        }
      }

      // Update status
      this.lastRefresh = new Date()
      this.updateRefreshStatus()

      // Auto-scroll if was at bottom
      if (wasAtBottom) {
        this.scrollToBottom()
      }

      // Re-apply search filter if active
      if (this.hasSearchFilterTarget && this.searchFilterTarget.value) {
        this.filterLogs()
      }
    } catch (error) {
      console.error("Error fetching logs:", error)
      if (this.hasRefreshStatusTarget) {
        this.refreshStatusTarget.textContent = "Error refreshing"
      }
    } finally {
      if (this.hasRefreshBtnTarget) {
        this.refreshBtnTarget.classList.remove("loading")
      }
    }
  }

  async refreshAllServices(lines) {
    // Get list of services from the dropdown options
    const services = Array.from(this.serviceFilterTarget.options)
      .map(opt => opt.value)
      .filter(val => val !== 'all')

    // Fetch logs from all services in parallel
    const fetchPromises = services.map(async (service) => {
      try {
        const url = `/instances/${this.instanceSlugValue}/logs?service=${service}&lines=${lines}`
        const response = await fetch(url, {
          headers: { 'Accept': 'text/plain' },
          credentials: 'same-origin'
        })
        if (response.ok) {
          const logs = await response.text()
          this.logsData[service] = logs
        }
      } catch (e) {
        console.error(`Failed to fetch logs for ${service}:`, e)
      }
    })

    await Promise.all(fetchPromises)
  }

  displayLogs(logs) {
    if (!logs) {
      this.logContentTarget.innerHTML = '<span class="opacity-50">No logs available</span>'
      return
    }

    const html = logs.split('\n').map(line => {
      let className = ''
      if (/error|Error|ERROR|exception|Exception|EXCEPTION|fatal|Fatal|FATAL/i.test(line)) {
        className = 'text-error'
      } else if (/warn|Warn|WARN/i.test(line)) {
        className = 'text-warning'
      } else if (/info|Info|INFO/i.test(line)) {
        className = 'text-info'
      } else if (/debug|Debug|DEBUG/i.test(line)) {
        className = 'opacity-60'
      }
      return `<div class="${className} hover:bg-white/5">${this.escapeHtml(line)}</div>`
    }).join('')

    this.logContentTarget.innerHTML = html
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  filterLogs() {
    const searchTerm = this.searchFilterTarget.value.toLowerCase()
    const lines = this.logContentTarget.querySelectorAll('div')

    lines.forEach(line => {
      if (!searchTerm || line.textContent.toLowerCase().includes(searchTerm)) {
        line.style.display = ''
        if (searchTerm) {
          line.classList.add('bg-yellow-500/20')
        } else {
          line.classList.remove('bg-yellow-500/20')
        }
      } else {
        line.style.display = 'none'
        line.classList.remove('bg-yellow-500/20')
      }
    })
  }

  clearSearch() {
    this.searchFilterTarget.value = ''
    this.filterLogs()
  }

  toggleAutoRefresh() {
    if (this.autoRefreshTarget.checked) {
      this.startAutoRefresh()
    } else {
      this.stopAutoRefresh()
    }
  }

  startAutoRefresh() {
    this.stopAutoRefresh() // Clear any existing interval
    this.refreshInterval = setInterval(() => this.refresh(), 5000)
  }

  stopAutoRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
  }

  updateRefreshStatus() {
    const seconds = Math.floor((new Date() - this.lastRefresh) / 1000)
    let text
    if (seconds < 5) {
      text = 'Updated just now'
    } else if (seconds < 60) {
      text = `Updated ${seconds}s ago`
    } else {
      text = `Updated ${Math.floor(seconds / 60)}m ago`
    }
    this.refreshStatusTarget.textContent = text
  }

  isScrolledToBottom() {
    const container = this.logContainerTarget
    return container.scrollHeight - container.scrollTop <= container.clientHeight + 50
  }

  scrollToTop() {
    this.logContainerTarget.scrollTop = 0
  }

  scrollToBottom() {
    this.logContainerTarget.scrollTop = this.logContainerTarget.scrollHeight
  }

  copyLogs() {
    const logContent = this.logContentTarget.textContent
    navigator.clipboard.writeText(logContent).then(() => {
      // Show feedback - find the copy button
      const btn = this.element.querySelector('[data-action*="copyLogs"]')
      if (btn) {
        const originalHTML = btn.innerHTML
        btn.innerHTML = '<i class="fas fa-check"></i> Copied'
        setTimeout(() => { btn.innerHTML = originalHTML }, 1500)
      }
    })
  }
}
